'use client';

import { useCallback, useEffect, useMemo } from 'react';

import { submitFeatureLead } from '@/components/forms/FeatureLeadForm/submit';
import { track } from '@/lib/analytics/events';
import { ERROR_CONTACT_MESSAGE, SUCCESS_CONTACT_MESSAGE } from '@/lib/config/system';
import type { TFeatureLeadFormData } from '@/lib/interfaces/forms/featureLeadForm.types';
import type { TUseFormOptions } from '@/lib/interfaces/hooks/useForm.types';
import featureLeadSchema from '@/lib/packages/featureLead.schema';
import toaster from '@/lib/packages/toaster';

import useForm from './useForm';

const baseDefaults = {
  firstName: '',
  lastName: '',
  company: '',
  mobile: '',
  email: '',
  message: '',
  roles: [] as string[],
};

const focusOrder: (keyof TFeatureLeadFormData)[] = [
  'firstName',
  'lastName',
  'company',
  'roles',
  'mobile',
  'email',
  'message',
];

const useFeatureLeadForm = (feature: string) => {
  const onSubmit = useCallback(
    async (data: TFeatureLeadFormData) => {
      try {
        await submitFeatureLead({ ...data, feature });
        track.lead({
          form_id: `feature-${feature}`,
          form_name: `Feature form - ${feature}`,
          method: 'feature_form',
        });
        return toaster(SUCCESS_CONTACT_MESSAGE);
      } catch {
        return toaster(ERROR_CONTACT_MESSAGE);
      }
    },
    [feature],
  );

  const config: TUseFormOptions<TFeatureLeadFormData> = useMemo(
    () => ({
      onSubmit,
      defaultValues: {
        ...baseDefaults,
        feature,
        roles: feature === 'electronic-documents' ? ['Physical Supplier'] : [],
      },
      schema: featureLeadSchema,
      shouldFocusError: false,
    }),
    [feature, onSubmit],
  );

  const { form, handleSubmit } = useForm<TFeatureLeadFormData>(config);
  const { errors, submitCount } = form.formState;

  useEffect(() => {
    if (!submitCount || !errors) return;
    const target = focusOrder.find((field) => errors[field]);
    if (!target) return;
    const frame = requestAnimationFrame(() => form.setFocus(target));
    return () => cancelAnimationFrame(frame);
  }, [errors, submitCount, form]);

  return { form, handleSubmit };
};

export default useFeatureLeadForm;
